import React, { useState, useRef, useEffect } from 'react';
import styles from './CameraModal.module.css';
import { IoCamera, IoRefresh, IoClose } from 'react-icons/io5';
import cameraManager from '../../services/cameraManager';

const CameraModal = ({ isOpen, onClose, onSubmit, embedded = false }) => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [stream, setStream] = useState(null);
  const [streamId, setStreamId] = useState(null);
  const [capturedImages, setCapturedImages] = useState([]);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Get camera devices and start camera when modal opens
  useEffect(() => {
    let isMounted = true;
    
    if (isOpen) {
      const initCamera = async () => {
        try {
          const videoDevices = await cameraManager.getDevices();
          if (isMounted) {
            setDevices(videoDevices);
            if (videoDevices.length > 0) {
              const defaultDevice = videoDevices[0].deviceId;
              setSelectedDevice(defaultDevice);
              // Auto start camera with first device
              startCameraStream(defaultDevice);
            }
          }
        } catch (error) {
          console.error('Error initializing camera:', error);
        }
      };

      initCamera();
    }
    
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  // Cleanup when component unmounts or when modal closes
  useEffect(() => {
    if (!isOpen) {
      stopCameraStream();
      setCapturedImages([]);
    }
    
    return () => {
      stopCameraStream();
      setCapturedImages([]);
    };
  }, [isOpen]);

  const startCameraStream = async (deviceId = selectedDevice) => {
    try {
      // Stop any previous stream
      if (streamId) {
        await cameraManager.stopCamera(streamId);
      }
      
      // Start new stream
      const { stream: newStream, streamId: newStreamId } = 
        await cameraManager.startCamera(deviceId);
      
      setStream(newStream);
      setStreamId(newStreamId);
      setIsCameraActive(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        await videoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
        });
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setIsCameraActive(false);
    }
  };

  const stopCameraStream = () => {
    if (streamId) {
      console.log('CameraModal: Stopping camera stream', streamId);
      cameraManager.stopCamera(streamId);
      setStream(null);
      setStreamId(null);
      setIsCameraActive(false);
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.load(); // Force cleanup of video element
      }
    }
  };

  const handleDeviceChange = (e) => {
    const newDeviceId = e.target.value;
    setSelectedDevice(newDeviceId);
    
    // Restart camera with new device
    stopCameraStream();
    setTimeout(() => startCameraStream(newDeviceId), 100);
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current || !isCameraActive) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw video frame to canvas
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImages(prev => [...prev, imageData]);
  };

  const handleRemoveImage = (index) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (capturedImages.length > 0) {
      // Stop camera before submitting
      stopCameraStream();
      
      onSubmit(capturedImages);
      setCapturedImages([]);
    }
  };

  const handleClose = () => {
    // Stop camera before closing
    stopCameraStream();
    onClose();
  };

  if (!isOpen) return null;

  const content = (
    <>
      {!embedded && (
        <button className={styles.closeButton} onClick={handleClose}>
          <IoClose size={24} />
        </button>
      )}
      
      {!embedded && <h3>Capture Images</h3>}
      
      <div className={styles.cameraContainer}>
        {devices.length > 1 && (
          <select 
            className={styles.deviceSelect}
            value={selectedDevice}
            onChange={handleDeviceChange}
          >
            {devices.map(device => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Camera ${devices.indexOf(device) + 1}`}
              </option>
            ))}
          </select>
        )}

        <div className={styles.videoContainer}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={styles.video}
          />
          {isCameraActive && (
            <button 
              className={styles.captureButton}
              onClick={captureImage}
              disabled={!stream}
            >
              <IoCamera size={24} />
              <span>Capture</span>
            </button>
          )}
        </div>

        {capturedImages.length > 0 && (
          <div className={styles.actionButtons}>
            <button 
              className={styles.submitButton}
              onClick={handleSubmit}
            >
              Use {capturedImages.length} Photo{capturedImages.length > 1 ? 's' : ''}
            </button>
          </div>
        )}

        {capturedImages.length > 0 && (
          <div className={styles.capturedImagesGrid}>
            {capturedImages.map((src, index) => (
              <div key={index} className={styles.capturedImageItem}>
                <img 
                  src={src} 
                  alt={`Captured ${index + 1}`} 
                  className={styles.capturedImage}
                />
                <button
                  className={styles.removeButton}
                  onClick={() => handleRemoveImage(index)}
                  title="Remove photo"
                >
                  <IoClose size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
};

export default CameraModal;